import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BarChart3, Database } from 'lucide-react'
import { Area, AreaChart, BarChart, Bar, Cell, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts'
import { ResponsiveContainer, Tooltip } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'

import type { AdminStats } from '../types'

// Chart config for area chart (cotizaciones por día)
const areaChartConfig = {
  total: {
    label: 'Cotizaciones',
    color: '#0d9488', // Teal
  },
} satisfies ChartConfig

interface PricingChartsProps {
  stats?: AdminStats
  isLoading?: boolean
}

export function PricingCharts({ stats, isLoading }: PricingChartsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-[30%_1fr]">
        <Card className="p-6">
          <Skeleton className="h-4 w-40 mb-4" />
          <Skeleton className="h-[250px] w-full" />
        </Card>
        <Card className="p-6">
          <Skeleton className="h-4 w-40 mb-4" />
          <Skeleton className="h-[250px] w-full" />
        </Card>
      </div>
    )
  }

  if (!stats) return null

  // Máximo 5 categorías, ordenadas por total descendente
  const dataLab = [...stats.por_laboratorio]
    .sort((a, b) => b.total - a.total)
    .slice(0, 5)

  // Tonos teal: más oscuro (teal-800) a la izquierda, aclarando hacia la derecha
  const TEAL_SHADES = ['#115e59', '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4']

  const tieneDataLab = dataLab.length > 0
  const tieneDataDia = stats.por_dia.length > 0

  if (!tieneDataLab && !tieneDataDia) return null

  return (
    <div className="grid gap-4 md:grid-cols-[30%_1fr]">
      {tieneDataLab && (
        <Card className="p-6 hover:shadow-none border border-gray-300 dark:border-gray-700 flex flex-col">
          <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4" />Cotizaciones por laboratorio</h3>
          <ResponsiveContainer width="100%" className="flex-1 min-h-[300px]" height="100%">
            <BarChart data={dataLab} margin={{ left: 0, right: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="laboratorio" tick={{ fontSize: 12 }} />
              <YAxis hide />
              <Tooltip cursor={false} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {dataLab.map((_, i) => (
                  <Cell key={i} fill={TEAL_SHADES[Math.min(i, TEAL_SHADES.length - 1)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Area Chart - Cotizaciones por Día (mismo estilo que registros) */}
      {tieneDataDia ? (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">Tendencia diaria</h3>
          </CardHeader>
          <CardContent>
            <ChartContainer config={areaChartConfig}>
              <AreaChart
                accessibilityLayer
                data={stats.por_dia}
                margin={{
                  left: 12,
                  right: 12,
                  top: 20,
                }}
              >
                <defs>
                  <linearGradient id="fillTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-total)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-total)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="dia"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => {
                    const date = new Date(value)
                    return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
                  }}
                />
                <ChartTooltip
                  cursor={{ stroke: '#57a6d8', strokeWidth: 1, strokeDasharray: '4 4' }}
                  content={<ChartTooltipContent indicator="dot" />}
                  labelFormatter={(value) => {
                    const date = new Date(value)
                    return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })
                  }}
                />
                <Area
                  dataKey="total"
                  type="natural"
                  fill="url(#fillTotal)"
                  fillOpacity={1}
                  stroke="var(--color-total)"
                  strokeWidth={2}
                  dot={({ cx, cy }: { cx: number; cy: number }) => (
                    <g>
                      <circle cx={cx} cy={cy} r={5} fill="#fff" />
                      <circle cx={cx} cy={cy} r={3} fill="var(--color-total)" />
                    </g>
                  )}
                  activeDot={({ cx, cy }: { cx: number; cy: number }) => (
                    <g>
                      <circle cx={cx} cy={cy} r={6} fill="#fff" />
                      <circle cx={cx} cy={cy} r={4} fill="var(--color-total)" />
                    </g>
                  )}
                >
                  <LabelList
                    dataKey="total"
                    position="top"
                    offset={10}
                    className="fill-foreground"
                    fontSize={11}
                  />
                </Area>
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">Tendencia diaria</h3>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-[250px] text-muted-foreground">
              <div className="text-center space-y-2">
                <Database className="h-12 w-12 mx-auto" />
                <p>Sin datos disponibles</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
